import { Option } from '@inrev/core';
import { Info } from 'luxon';
import * as z from 'zod';
import { nameSuffixDef, nameSuffixSchema, financialPeriodPreparationMethodSchema } from '../../../enums.mjs';
import { decryptedSSN, email, isoMonthDaySchema, naicsCodeId } from '../../opaque.mjs';
import { nonNegativeCurrencySchema } from '../../utils.mjs';
import { schema } from '../account/requested-limits.mjs';
import { ownerResidenceStatusDef, statementFrequencyDef } from './application-extraction-output.mjs';

const SPECIALTY_REQUIRED_ERROR = "Pick at least one specialty.";
const MIDDLE_INITIAL_ERROR = "Enter one letter.";
const SPOUSE_NAME_REQUIRED_ERROR = "Add the spouse's first and last name, or clear the other spouse fields.";
const FISCAL_YEAR_END_ERROR = "Enter a month and day, like 06/30.";
const CREDIT_REPORT_PERMISSION_ERROR = "Confirm credit report permission for each owner.";
const SSN_ERROR = "Enter a 9-digit SSN, like 123-45-6789.";
const DUPLICATE_SSN_ERROR = "Another owner has this SSN.";
const suffixes = new Set(nameSuffixDef);
const isSuffix = (token) => suffixes.has(token);
const isInitial = (token) => /^[A-Za-z]\.?$/.test(token);
const suffixOf = (token) => {
    const normalized = token?.replace(/\./g, "").toUpperCase();
    return Option.isSome(normalized) && isSuffix(normalized) ? normalized : undefined;
};
const toFirstLastOrder = (fullName) => {
    const [lastPart, firstPart, ...more] = fullName.split(",");
    if (Option.isNone(firstPart) || more.length > 0 || Option.isSome(suffixOf(firstPart.trim())))
        return fullName;
    return `${firstPart} ${lastPart}`;
};
const splitPersonName = (fullName) => {
    const tokens = toFirstLastOrder(fullName.trim()).replace(/,/g, " ").split(/\s+/).filter(Boolean);
    const suffix = suffixOf(tokens[tokens.length - 1]);
    const nameTokens = Option.isSome(suffix) ? tokens.slice(0, -1) : tokens;
    const [first, ...rest] = nameTokens;
    const lastName = rest.pop();
    if (Option.isNone(first) || Option.isNone(lastName))
        return undefined;
    const [onlyMiddle] = rest;
    const middleInitial = rest.length === 1 && Option.isSome(onlyMiddle) && isInitial(onlyMiddle)
        ? onlyMiddle.charAt(0).toUpperCase()
        : undefined;
    const firstName = Option.isSome(middleInitial) ? first : [first, ...rest].join(" ");
    return { firstName, middleInitial, lastName, suffix };
};
const monthNumbers = new Map([
    ...[
        ...Info.months("long", { locale: "en-US" }),
        ...Info.months("short", { locale: "en-US" }),
    ].map((name, i) => [name.toLowerCase(), (i % 12) + 1]),
    ["sept", 9],
]);
const monthAndDayOf = (text) => {
    const numeric = /^(\d{1,2})[-/](\d{1,2})(?:[-/](?:\d{2}|\d{4}))?$/.exec(text);
    if (numeric)
        return [Number(numeric[1]), Number(numeric[2])];
    const named = /^([a-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+\d{4})?$/i.exec(text);
    return Option.flatMap(named, ([, name = "", day]) => Option.map(monthNumbers.get(name.toLowerCase()), (month) => [
        month,
        Number(day),
    ]));
};
const parseMonthDay = (text) => Option.flatMap(monthAndDayOf(text.trim()), ([month, day]) => {
    const parsed = isoMonthDaySchema.safeParse(`${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
    return parsed.success ? parsed.data : undefined;
});
const ssnSeparators = /[\s-]/g;
const nineDigitSsn = z
    .string()
    .regex(/^\d{9}$/, SSN_ERROR)
    .pipe(decryptedSSN);
const normalizeSsn = (text) => {
    const parsed = nineDigitSsn.safeParse(text.replace(ssnSeparators, ""));
    return parsed.success ? parsed.data : undefined;
};
const findDuplicateSsnIndexes = (owners) => {
    const indexesByDigits = new Map();
    owners.forEach((owner, index) => {
        const digits = Option.isSome(owner.ssn) ? normalizeSsn(owner.ssn) : undefined;
        if (Option.isNone(digits))
            return;
        indexesByDigits.set(digits, [...(indexesByDigits.get(digits) ?? []), index]);
    });
    return new Set([...indexesByDigits.values()].filter((group) => group.length > 1).flat());
};
const formatSsn = (text) => Option.mapOrElse(normalizeSsn(text), (digits) => `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`, text);
const maskSsn = (text) => `***-**-${text.replace(/\D/g, "").slice(-4).padStart(4, "*")}`;
const deriveModelSize = (aggregateLimit) => {
    if (aggregateLimit <= 500_000)
        return "small";
    if (aggregateLimit <= 1_000_000)
        return "medium";
    return "large";
};
const requiredText = z.string().trim().min(1, "Required");
const optionalText = z.string().trim().nullish();
const middleInitial = z.preprocess((value) => (value === "" ? null : value), z
    .string()
    .trim()
    .regex(/^[A-Za-z]$/, MIDDLE_INITIAL_ERROR)
    .nullish());
const suffix = z.preprocess((value) => (value === "" ? null : value), nameSuffixSchema.nullish());
const optionalEmail = z.union([z.literal(""), email]).nullish();
const optionalSsn = z.preprocess((value) => (typeof value === "string" ? value.replace(ssnSeparators, "") || null : value), nineDigitSsn.nullish());
const optionalMonthDay = z.preprocess((value) => (value === "" ? null : value), z
    .string()
    .refine((text) => isoMonthDaySchema.safeParse(text).success, FISCAL_YEAR_END_ERROR)
    .pipe(isoMonthDaySchema)
    .nullish());
const ApplicationReviewAccount = schema.safeExtend({
    name: requiredText,
    federalTaxId: optionalText,
    businessAddress: optionalText,
});
const ApplicationReviewOwner = z.object({
    firstName: requiredText,
    middleInitial,
    lastName: requiredText,
    suffix,
    ownershipPct: z.number().min(0).max(100).nullish(),
    homeAddress: optionalText,
    ownOrRent: z.enum(ownerResidenceStatusDef).nullish(),
    email: optionalEmail,
    spouseFirstName: optionalText,
    spouseMiddleInitial: middleInitial,
    spouseLastName: optionalText,
    spouseSuffix: suffix,
    spouseEmail: optionalEmail,
    spouseAddress: optionalText,
    ssn: optionalSsn,
    creditReportPermission: z.literal(true, { error: CREDIT_REPORT_PERMISSION_ERROR }),
});
const isBlank = (text) => Option.isNone(text) || text === "";
const requireSpouseName = (owner, ctx) => {
    const hasDetails = [
        owner.spouseFirstName,
        owner.spouseMiddleInitial,
        owner.spouseLastName,
        owner.spouseSuffix,
        owner.spouseEmail,
        owner.spouseAddress,
    ].some((detail) => !isBlank(detail));
    if (!hasDetails)
        return;
    if (isBlank(owner.spouseFirstName))
        ctx.addIssue({
            code: "custom",
            path: ["spouseFirstName"],
            message: SPOUSE_NAME_REQUIRED_ERROR,
        });
    if (isBlank(owner.spouseLastName))
        ctx.addIssue({ code: "custom", path: ["spouseLastName"], message: SPOUSE_NAME_REQUIRED_ERROR });
};
const ApplicationReviewAffiliate = z.object({
    name: requiredText,
    federalTaxId: optionalText,
    address: optionalText,
});
const ApplicationReviewExperience = z.object({
    naicsCodes: z.array(naicsCodeId).min(1, SPECIALTY_REQUIRED_ERROR),
    yearsUnderCurrentManagement: optionalText,
    largestJobAmount: nonNegativeCurrencySchema.nullish(),
    largestJobDescription: optionalText,
    failedToCompleteJob: z.boolean().nullish(),
    defaultedOnContract: z.boolean().nullish(),
    lostPaymentSuit: z.boolean().nullish(),
    causedSuretyLoss: z.boolean().nullish(),
    failedInBusiness: z.boolean().nullish(),
    grossLossOver10PctLast36Months: z.boolean().nullish(),
    expectsGrossLossOnCurrentProject: z.boolean().nullish(),
});
const ApplicationReviewFinancials = z.object({
    statementType: financialPeriodPreparationMethodSchema.nullish(),
    statementFrequency: z.enum(statementFrequencyDef).nullish(),
    fiscalYearEnd: optionalMonthDay,
    profitableYtd: z.boolean().nullish(),
    profitableLastFye: z.boolean().nullish(),
    lineOfCreditAmount: nonNegativeCurrencySchema.nullish(),
});
const ApplicationReview = z.object({
    account: ApplicationReviewAccount,
    owners: z.array(ApplicationReviewOwner.superRefine(requireSpouseName)),
    affiliates: z.array(ApplicationReviewAffiliate),
    experience: ApplicationReviewExperience,
    financials: ApplicationReviewFinancials,
});

export { ApplicationReview, ApplicationReviewAccount, ApplicationReviewAffiliate, ApplicationReviewExperience, ApplicationReviewFinancials, ApplicationReviewOwner, CREDIT_REPORT_PERMISSION_ERROR, DUPLICATE_SSN_ERROR, FISCAL_YEAR_END_ERROR, MIDDLE_INITIAL_ERROR, SPECIALTY_REQUIRED_ERROR, SPOUSE_NAME_REQUIRED_ERROR, SSN_ERROR, deriveModelSize, findDuplicateSsnIndexes, formatSsn, maskSsn, normalizeSsn, parseMonthDay, requireSpouseName, splitPersonName };
